// Makes a dashboard of tiles resizable, paged and bounded.
//
// The complaint this answers: a tile that lists every record it has is not a tile, it is a page
// with a border. "My players" with twenty-four children pushed everything else off the screen.
// Each tile now shows as many rows as it has room for, pages through the rest, and can be
// dragged by either bottom corner — the number of rows follows the height, the neighbours get
// out of the way, and the whole thing floats back up into the gaps.
//
// Rows are rendered by the server and windowed here. At the sizes these lists reach (a family's
// children, a season's registrations) fetching a window per resize would be a round trip to
// save nothing; the mockup's fetch-on-resize architecture is the right answer when a tile can
// outgrow one response, and `docs/mockups/guardian/dashboard-tiles.html` keeps that design.
//
// CSS Grid cannot transition `grid-area`, so the settle after a resize is done with FLIP:
// measure, invert with a transform, release. Without it the neighbours jump.
import { COLS, MIN_W, MIN_H, resize, merge, compact } from './grid-layout.js';

const ROW = 22;
const GAP = 14;
const ROW_H = 38;
const STORE = 'rr.dashboard.layout';

const phone = () => window.matchMedia('(max-width: 767.98px)').matches;
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function load(key) {
  try {
    return JSON.parse(window.localStorage.getItem(`${STORE}.${key}`) || 'null');
  } catch {
    return null;
  }
}
function save(key, layout) {
  try {
    window.localStorage.setItem(`${STORE}.${key}`, JSON.stringify(layout));
  } catch {
    // A private window, or storage the browser has blocked. The layout simply does not persist.
  }
}

export function initDashboardGrid(root = document) {
  const grid = root.querySelector?.('[data-dash-grid]');
  if (!grid) return;
  const key = grid.dataset.dashGrid || 'default';
  const strings = (() => {
    try {
      return JSON.parse(root.getElementById('dash-strings')?.textContent || '{}');
    } catch {
      return {};
    }
  })();
  const say = (name, vars = {}) =>
    String(strings[name] ?? '').replace(/\{\{(\w+)\}\}/g, (_, n) => String(vars[n] ?? ''));

  const tiles = [...grid.querySelectorAll('[data-tile]')];
  if (!tiles.length) return;
  const defaults = tiles.map((node) => ({
    id: node.dataset.tile,
    x: Number(node.dataset.x) || 0,
    y: Number(node.dataset.y) || 0,
    w: Number(node.dataset.w) || 12,
    h: Number(node.dataset.h) || 14,
  }));
  let layout = merge(defaults, load(key));
  const nodes = new Map(tiles.map((n) => [n.dataset.tile, n]));
  const at = (id) => layout.find((l) => l.id === id);

  // ── Paging ────────────────────────────────────────────────────────────────────────────────
  const pages = new Map(); // id -> { rows, page }
  for (const node of tiles) {
    const list = node.querySelector('[data-tile-rows]');
    if (!list) continue;
    pages.set(node.dataset.tile, { rows: [...list.children], page: 0 });
  }

  function capacity(node) {
    const body = node.querySelector('[data-tile-body]');
    const px = body ? body.clientHeight : 0;
    return Math.max(1, Math.floor((px || 200) / ROW_H));
  }

  function paint(id) {
    const node = nodes.get(id);
    const state = pages.get(id);
    if (!node || !state || !state.rows.length) return;
    const per = capacity(node);
    const last = Math.max(0, Math.ceil(state.rows.length / per) - 1);
    state.page = Math.min(state.page, last);
    const first = state.page * per;
    state.rows.forEach((row, index) => {
      row.hidden = index < first || index >= first + per;
    });
    const count = node.querySelector('[data-tile-count]');
    const prev = node.querySelector('[data-tile-page="prev"]');
    const next = node.querySelector('[data-tile-page="next"]');
    const foot = node.querySelector('[data-tile-foot]');
    if (foot) foot.hidden = state.rows.length <= per;
    if (count)
      count.textContent = say('showing', {
        from: first + 1,
        to: Math.min(first + per, state.rows.length),
        total: state.rows.length,
      });
    if (prev) prev.disabled = state.page === 0;
    if (next) next.disabled = state.page >= last;
  }

  // ── Placing ───────────────────────────────────────────────────────────────────────────────
  function place(animate) {
    if (phone()) {
      for (const node of nodes.values()) node.style.gridArea = '';
      for (const id of pages.keys()) paint(id);
      return;
    }
    const before = animate
      ? new Map([...nodes].map(([id, n]) => [id, n.getBoundingClientRect()]))
      : null;
    for (const item of layout) {
      const node = nodes.get(item.id);
      if (node)
        node.style.gridArea = `${item.y + 1} / ${item.x + 1} / span ${item.h} / span ${item.w}`;
    }
    for (const id of pages.keys()) paint(id);
    if (!before || reduced()) return;
    for (const [id, node] of nodes) {
      const a = before.get(id);
      const b = node.getBoundingClientRect();
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      if (!dx && !dy) continue;
      node.classList.remove('is-settling');
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      requestAnimationFrame(() => {
        node.classList.add('is-settling');
        node.style.transform = '';
      });
    }
  }

  // ── Resizing ──────────────────────────────────────────────────────────────────────────────
  const cellWidth = () => (grid.clientWidth - GAP * (COLS - 1)) / COLS;
  let drag = null;
  let saveTimer = 0;

  function apply(id, next, announce) {
    const { changed } = resize(layout, id, next);
    if (!changed) return;
    place(true);
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => save(key, layout), 600);
    const item = at(id);
    const live = root.querySelector('[data-dash-live]');
    if (live && announce)
      live.textContent = say('resized', {
        name: nodes.get(id)?.dataset.tileName || id,
        w: item.w,
        h: item.h,
        n: capacity(nodes.get(id)),
      });
  }

  grid.addEventListener('pointerdown', (event) => {
    const handle = event.target.closest('[data-dash-handle]');
    if (!handle || phone()) return;
    const id = handle.closest('[data-tile]').dataset.tile;
    const item = at(id);
    drag = {
      id,
      side: handle.dataset.dashHandle,
      x0: event.clientX,
      y0: event.clientY,
      w0: item.w,
      h0: item.h,
      cx0: item.x,
    };
    handle.setPointerCapture(event.pointerId);
    nodes.get(id).classList.add('is-resizing');
    event.preventDefault();
  });

  grid.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const dCols = Math.round((event.clientX - drag.x0) / (cellWidth() + GAP));
    const dRows = Math.round((event.clientY - drag.y0) / (ROW + GAP));
    if (drag.side === 'se')
      apply(drag.id, { w: drag.w0 + dCols, h: drag.h0 + dRows, x: drag.cx0 }, false);
    else apply(drag.id, { w: drag.w0 - dCols, h: drag.h0 + dRows, x: drag.cx0 + dCols }, false);
  });

  function endDrag() {
    if (!drag) return;
    nodes.get(drag.id)?.classList.remove('is-resizing');
    apply(drag.id, { ...at(drag.id) }, true);
    save(key, layout);
    drag = null;
  }
  grid.addEventListener('pointerup', endDrag);
  grid.addEventListener('pointercancel', endDrag);

  // A drag-only affordance cannot be used with a keyboard, so the handles are real buttons and
  // the arrow keys resize.
  grid.addEventListener('keydown', (event) => {
    const handle = event.target.closest('[data-dash-handle]');
    if (!handle || phone()) return;
    const step = { ArrowRight: [1, 0], ArrowLeft: [-1, 0], ArrowDown: [0, 1], ArrowUp: [0, -1] }[
      event.key
    ];
    if (!step) return;
    event.preventDefault();
    const id = handle.closest('[data-tile]').dataset.tile;
    const item = at(id);
    const [dx, dy] = step;
    if (handle.dataset.dashHandle === 'sw')
      apply(id, { w: item.w - dx, h: item.h + dy, x: item.x + dx }, true);
    else apply(id, { w: item.w + dx, h: item.h + dy, x: item.x }, true);
  });

  grid.addEventListener('click', (event) => {
    const pager = event.target.closest('[data-tile-page]');
    if (!pager) return;
    const id = pager.closest('[data-tile]').dataset.tile;
    const state = pages.get(id);
    state.page += pager.dataset.tilePage === 'next' ? 1 : -1;
    state.page = Math.max(0, state.page);
    paint(id);
  });

  const reset = root.querySelector('[data-dash-reset]');
  reset?.addEventListener('click', () => {
    layout = compact(defaults.map((d) => ({ ...d })));
    save(key, layout);
    place(true);
    // Always say so, even when the layout was already the default: a button that sometimes
    // does nothing visible is indistinguishable from one that is broken.
    const live = root.querySelector('[data-dash-live]');
    if (live) live.textContent = say('wasReset');
  });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => place(false), 120);
  });

  grid.classList.add('is-live');
  place(false);
}

export { COLS, MIN_W, MIN_H };
